import { useMutation } from "@tanstack/react-query"
import { Link, useLocation, useNavigate } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  deleteGameNotification,
  markGameNotificationRead,
} from "@/api/gameNotificationsApi"
import {
  formatGameNotificationLinkLabel,
  openResolvedGameNotificationLink,
  resolveGameNotificationLink,
} from "@/utils/gameNotificationLink"
import { GuardiansStackParamList } from "@/types/guardiansNavigation"

type DetailState = GuardiansStackParamList["GameNotificationDetail"]

export default function GameNotificationDetailPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const params = (location.state ?? null) as DetailState | null

  const markRead = useMutation({
    mutationFn: () => markGameNotificationRead(params!.notificationId),
  })

  const remove = useMutation({
    mutationFn: () => deleteGameNotification(params!.notificationId),
    onSuccess: () => navigate("/play/notifications", { replace: true }),
  })

  if (!params) {
    return (
      <GameScreenLayout title="Уведомление" backTo="/play/notifications">
        <p className="text-sm text-guardians-muted">
          Откройте уведомление из списка.
        </p>
        <Link to="/play/notifications" className="mt-4 text-guardians-accent underline">
          К списку
        </Link>
      </GameScreenLayout>
    )
  }

  const resolved = resolveGameNotificationLink(params.link)
  const canOpen = resolved.kind !== "none"

  return (
    <GameScreenLayout title="Уведомление" backTo="/play/notifications">
      <p className="text-xs text-guardians-muted">{params.from}</p>
      <h2 className="mt-2 text-lg font-medium">{params.title}</h2>
      <p className="mt-3 whitespace-pre-wrap text-sm text-guardians-muted">
        {params.body}
      </p>
      <p className="mt-2 text-xs text-guardians-muted">
        {new Date(params.date).toLocaleString("ru-RU")}
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        {canOpen ? (
          <button
            type="button"
            className="rounded-xl bg-guardians-accent px-4 py-2 text-sm text-guardians-bg"
            onClick={() => {
              void markRead.mutateAsync().finally(() => {
                openResolvedGameNotificationLink(resolved)
              })
            }}
          >
            {formatGameNotificationLinkLabel(params.link) || "Перейти"}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => markRead.mutate()}
          className="rounded-xl border border-white/20 px-4 py-2 text-sm"
        >
          Прочитано
        </button>
        <button
          type="button"
          onClick={() => remove.mutate()}
          className="rounded-xl border border-red-500/40 px-4 py-2 text-sm text-red-200"
        >
          Удалить
        </button>
      </div>
    </GameScreenLayout>
  )
}
